import { useEffect, useState, type ReactNode } from 'react';
import { Check, CloudSun, HardHat, Hourglass, Mail, User, X } from 'lucide-react';
import { Module } from './ui/Module';
import { DecisionBar } from './ui/DecisionBar';
import { api, ApiError } from '../lib/api';
import { uploadFile } from '../lib/upload';
import { useI18n } from '../lib/i18n';
import { CopyBtn } from './ui';
import { statusColor } from '../lib/status';
import { PhoneLink } from './PhoneLink';
import { FileDrop } from './FileDrop';
import { ClaimChat } from './ClaimChat';
import { ClaimPhotosBox } from './ClaimPhotosBox';
import { InfoPendingBox } from './InfoPendingBox';
import { ContractorInvoicesBox } from './ContractorInvoicesBox';

import { ClaimServices } from './ClaimServices';
import { ClaimWeather } from './ClaimWeather';
import { ClaimFiles } from './ClaimFiles';
import { ClaimEmailBox } from './ClaimEmailBox';
import { CaseOverview } from './CaseOverview';
import { ContractValidation } from './ContractValidation';
import { ClaimAtrBox } from './ClaimAtrBox';
import { ClaimLossInfo } from './ClaimLossInfo';
import { IntakeSubmissionBox } from './IntakeSubmissionBox';
import { ContractPanel } from './ContractPanel';
import { WelcomeCallBox } from './WelcomeCallBox';
import { AccountManagerPicker } from './AccountManagerPicker';
import { PolicyReader } from './PolicyReader';
import { ClaimPolicySummary } from './ClaimPolicySummary';
import { PropertyRoofView } from './PropertyRoofView';
import { ValidationChecklist } from './ValidationChecklist';
import { ImproveTextButton } from './ImproveTextButton';

type Summary = {
  homeowner_name: string | null; homeowner_email: string | null; phone: string | null;
  address: string | null; preferred_language: string | null; claim_type: string | null; type_of_loss: string | null;
  lifecycle_status?: string | null; current_status?: string | null;
};

/**
 * Dos tarjetas lado a lado que caen a una sola columna cuando no hay ancho.
 *
 * `auto-fit` + `minmax` en vez de un breakpoint fijo: lo que manda es el ancho del CONTENEDOR,
 * no el de la ventana, y este detalle se abre tanto a pantalla completa como dentro del drawer
 * angosto del claim. `align-items: start` evita que la tarjeta corta se estire al alto de la
 * larga y quede con medio cuerpo vacío.
 */
const Par = ({ children }: { children: ReactNode }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 8, alignItems: 'start' }}>{children}</div>
);

/**
 * Vista expandida de una fila de Intake según su etapa (Lead / Contract Sent /
 * Contract Signed). Cada etapa muestra sus acciones propias. Ver [[intake-contract-esign]].
 */
export function IntakeRowDetail({
  claimId, status, address, contractorName, accountManager, onChanged,
}: {
  claimId: string; status: string; address: string | null;
  contractorName: string | null; accountManager: string | null; onChanged: () => void;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [denying, setDenying] = useState(false);   // muestra el campo de "reason why" del deny
  const [denyReason, setDenyReason] = useState('');
  const [msg, setMsg] = useState('');
  const [fee, setFee] = useState('');
  const [feeSource, setFeeSource] = useState<string>('none');
  // `from`/`team`: DE DÓNDE sale el PA impreso (equipo asignado = la fuente; el resto son
  // respaldos). Se muestra para que nadie edite la copia de la ficha del contratista creyendo
  // que decide. `blocked`: el PA del equipo no tiene licencia en el estado → no habrá contrato.
  const [pa, setPa] = useState<{
    name: string | null; license: string | null; missing: boolean; state: string | null;
    from: string | null; team: string | null; blocked: { pa_name: string; team_name: string } | null;
  }>({ name: null, license: null, missing: false, state: null, from: null, team: null, blocked: null });
  // La plantilla que va a salir (null hasta que responde la sugerencia).
  const [tpl, setTpl] = useState<{ id: string; name: string; special_for: string | null } | null>(null);
  // El estado del form contradice el de la dirección → el backend no va a generar el contrato.
  const [choqueEstado, setChoqueEstado] = useState<{ form: string; address: string } | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);

  useEffect(() => {
    if (status === 'Lead') {
      // Sugiere el fee% por defecto (contratista o default del estado) + el PA que saldrá
      // en el contrato (por LICENCIA del estado) para verlo antes de enviar.
      api.get<{ fee_percent: number | null; source: string; pa_name: string | null; pa_license: string | null; pa_missing: boolean; state: string | null; pa_from: string | null; pa_team: string | null; pa_blocked: { pa_name: string; team_name: string } | null; template: { id: string; name: string; special_for: string | null } | null; state_mismatch: { form: string; address: string } | null }>(`/api/claims/${claimId}/contract/suggestion`)
        .then((r) => {
          setFee(r.fee_percent != null ? String(r.fee_percent) : ''); setFeeSource(r.source);
          setPa({
            name: r.pa_name ?? null, license: r.pa_license ?? null, missing: Boolean(r.pa_missing), state: r.state ?? null,
            from: r.pa_from ?? null, team: r.pa_team ?? null, blocked: r.pa_blocked ?? null,
          });
          setTpl(r.template ?? null);
        setChoqueEstado(r.state_mismatch ?? null);
        }).catch(() => {});
    }
    // Info del homeowner (para el módulo de datos en Lead) + estado real del claim (para mostrar
    // en Waiting Documents el claim status y el payment status en el que ESTÁ, sin moverlo).
    if (status === 'Lead' || status === 'Waiting Documents') {
      api.get<Summary>(`/api/claims/${claimId}/summary`).then((r) => setSummary(r)).catch(() => {});
    }
  }, [claimId, status]);

  async function sendContract() {
    setBusy(true); setErr(''); setMsg('');
    try {
      const feePercent = fee.trim() === '' ? null : Number(fee);
      const cv = await api.post<{ id: string }>(`/api/claims/${claimId}/contract/prepare`, { feePercent });
      await api.post(`/api/contracts/${cv.id}/send`, {}); onChanged();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function uploadSigned(file: File) {
    setBusy(true); setErr(''); setMsg('');
    try { const key = await uploadFile(file, 'misc'); await api.post(`/api/claims/${claimId}/contract/upload-signed`, { fileKey: key }); onChanged(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function forceReview() {
    if (!window.confirm(t('ird.forceReviewConfirm'))) return;
    setBusy(true); setErr(''); setMsg('');
    try { await api.post(`/api/claims/${claimId}/force-claim-review`, {}); onChanged(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function waitDocs() {
    if (!window.confirm(t('ird.waitForDocsConfirm'))) return;
    setBusy(true); setErr(''); setMsg('');
    try { await api.post(`/api/claims/${claimId}/wait-for-docs`, {}); onChanged(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function decide(decision: 'accepted_new' | 'accepted_existing' | 'denied', reason?: string) {
    setBusy(true); setErr(''); setMsg('');
    try { await api.post(`/api/claims/${claimId}/claim-review-decision`, { decision, reason: reason?.trim() || undefined }); onChanged(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  const row: React.CSSProperties = { fontSize: 'var(--fs-xs)' };
  const err_ = err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{err}</div>;
  const ok_ = msg && <div style={{ color: 'var(--green)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'] }}>{msg}</div>;
  // Módulo de Updates consistente en todas las etapas: en su propia tarjeta y plegado por defecto.
  const updatesCard = <div className="card"><ClaimChat claimId={claimId} channel="intake" title="Intake Department Updates" /></div>;

  // ---- Lead ----
  if (status === 'Lead') {
    return (
      <div style={{ display: 'grid', gap: 12, padding: '4px 0' }}>
        {/* LO QUE ESCRIBIÓ EL CLIENTE, PRIMERO — pedido del equipo de Intake (2026-08-21): "las
            preguntas del formulario las quieren al tope de la sección, y eso tiene que llevar el
            link del form que llenaron". Estaba a media pantalla, debajo del contrato, las fotos y
            los archivos: lo primero que hace intake al abrir un lead es leer lo que la persona
            contestó, así que ahora abre expandido y con el link arriba (colapsado, el link al
            formulario tampoco se veía). */}
        <IntakeSubmissionBox claimId={claimId} defaultOpen />

        {/* Fila superior: 3 módulos. alignItems:start → cada tarjeta a su ALTURA NATURAL (antes
            'stretch' estiraba todas a la más alta, dejando huecos y viéndose disparejo). */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, alignItems: 'start' }}>
          {/* Contrato / acciones (el contratista y la dirección van en la cabecera de la fila). */}
          <div className="card" style={{ display: 'grid', gap: 6 }}>
            <AccountManagerPicker claimId={claimId} initial={accountManager} onChanged={onChanged} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ ...row, display: 'flex', gap: 6, alignItems: 'center' }}>
                <span className="muted">{t('ird.fee')}:</span>
                <input className="input sm" style={{ width: 70 }} type="number" step="0.1" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="%" />
                <span className="muted">%</span>
              </label>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                {feeSource === 'contractor' ? t('ird.feeFromContractor') : feeSource === 'state_default' ? t('ird.feeFromState') : t('ird.feeNone')}
              </span>
            </div>
            {/* PA que saldrá en el contrato: quién es, con qué licencia y DE DÓNDE sale. El que
                firma es este mismo (nadie firma por otro), así que si dice "respaldo" conviene
                asignarle equipo al claim antes de mandarlo. */}
            {pa.blocked ? (
              <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--red)', fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'] }}>
                {t('ct.paBlockedWarn', { pa: pa.blocked.pa_name, team: pa.blocked.team_name, state: pa.state ?? '—' })}
              </div>
            ) : pa.missing ? (
              <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--red)', fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'] }}>{t('ct.paMissingWarn', { state: pa.state ?? '—' })}</div>
            ) : pa.name ? (
              <div style={{ fontSize: 'var(--fs-2xs)' }}>
                <span className="muted">{t('ct.paLabel')}: </span><strong>{pa.name}</strong>
                {pa.license ? <span className="muted"> · Lic. {pa.license}{pa.state ? ` (${pa.state})` : ''}</span> : null}
                <span className="muted"> · {pa.from === 'team' ? t('ct.paFromTeam', { team: pa.team ?? '—' }) : t('ct.paFromBackup')}</span>
              </div>
            ) : null}
            {/* El estado del form contra el de la DIRECCIÓN. La plantilla se elige por el estado, así
                que un dropdown mal contestado manda el contrato de otro estado — y eso se descubría
                leyendo el PDF que el cliente ya había firmado (claims 2394 y 2437, contrato de PA
                sobre casas de GA). El backend ya no lo genera; acá se dice por qué, antes de apretar. */}
            {choqueEstado && (
              <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--red)', fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'] }}>
                {t('ct.stateMismatchWarn', { form: choqueEstado.form, address: choqueEstado.address })}
              </div>
            )}
            {/* QUÉ contrato va a salir. Solo se muestra cuando NO es la plantilla genérica del
                estado: un contratista con contrato especial (cláusula propia) tiene que verse
                ANTES de mandarlo, no descubrirse leyendo el PDF que el cliente ya firmó. */}
            {tpl?.special_for && (
              <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--orange)', fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'] }}>
                <HardHat size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('ct.specialFor', { name: tpl.special_for })}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
              <button className="btn sm" disabled={busy || pa.missing || !!pa.blocked || !!choqueEstado} onClick={sendContract}><Mail size={14} strokeWidth={1.75} /> {t('al.sendContract')}</button>
              <FileDrop onFile={uploadSigned} accept="application/pdf" label={t('cx.uploadSigned')} busy={busy} busyLabel={t('cd.uploading')} compact />
            </div>
            {err_}{ok_}
          </div>

          {/* Info del homeowner — módulo estándar del rediseño (colapsable con preview). */}
          <Module id={`homeowner:${claimId}`} icon={User} title={t('ird.homeowner')}
            preview={`${summary?.homeowner_name || '—'} · ${summary?.phone || summary?.homeowner_email || '—'}`}>
            <div style={{ display: 'grid', gap: 6, paddingTop: 2 }}>
              <div style={row}><span className="muted">{t('ird.hoName')}: </span>{summary?.homeowner_name || '—'}</div>
              <div style={row}><span className="muted">{t('ird.hoEmail')}: </span>{summary?.homeowner_email ? <a href={`mailto:${summary.homeowner_email}`} style={{ color: 'var(--accent)' }}>{summary.homeowner_email}</a> : '—'}</div>
              <div style={row}><span className="muted">{t('ird.hoPhone')}: </span>{summary?.phone ? <PhoneLink phone={summary.phone} claimId={claimId} recipientType="homeowner" style={{ color: 'var(--accent)' }} /> : '—'}</div>
              <div style={row}><span className="muted">{t('cci.language')}: </span>{summary?.preferred_language || '—'}</div>
              <div style={row}><span className="muted">{t('ird.typeOfClaim')}: </span>{summary?.claim_type || summary?.type_of_loss || '—'}</div>
            </div>
          </Module>

          {/* Fotos (los Documentos/Files se movieron a una fila completa abajo para tener espacio). */}
          <div className="card" style={{ display: 'grid', gap: 8 }}>
            <ClaimPhotosBox claimId={claimId} onChanged={() => onChanged()} compact />
          </div>
        </div>

        {/* Documentos/Files a lo ANCHO (abajo del módulo de contrato): los archivos se acomodan en
            grid de derecha a izquierda para que no se estire todo hacia abajo. */}
        <ClaimFiles claimId={claimId} onChanged={onChanged} wide />

        {/* Intake Department Updates + follow-up: faltaba en Lead (sí estaba en las demás etapas).
            ClaimChat ya trae la cabecera de follow-up (último/próximo + botón ⏰ Follow up). */}
        {updatesCard}

        <ClaimServices claimId={claimId} contractorName={contractorName} />
        <Module id={`storms:${claimId}`} icon={CloudSun} title={t('ird.storms')} preview={t('mod.stormsPreview')} defaultOpen={false}>
          <div style={{ marginTop: 6 }}><ClaimWeather claimId={claimId} /></div>
        </Module>
      </div>
    );
  }

  // ---- Contract Sent ----
  if (status === 'Contract Sent') {
    return (
      <div style={{ display: 'grid', gap: 12, padding: '4px 0' }}>
        {/* Módulos Contract + Welcome call (2 columnas). Cada uno trae su propia .card.
            ContractPanel ya muestra el checklist Prepared/Sent/Opened/Signed + Resend/View/Copy link. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, alignItems: 'start' }}>
          <ContractPanel claimId={claimId} onChanged={onChanged} />
          <WelcomeCallBox claimId={claimId} onChanged={onChanged} />
        </div>
        {err_}{ok_}
        <ClaimPhotosBox claimId={claimId} onChanged={() => onChanged()} compact />
        {updatesCard}
        <ClaimEmailBox claimId={claimId} departmentKey="intake" />
      </div>
    );
  }

  // ---- Contract Signed ----
  if (status === 'Contract Signed') {
    return (
      <div style={{ display: 'grid', gap: 12, padding: '4px 0' }}>
        {/* Módulos Contract + Welcome call (2 columnas, como en el claim completo).
            Cada componente ya trae su propia .card. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, alignItems: 'start' }}>
          <ContractPanel claimId={claimId} onChanged={onChanged} />
          <WelcomeCallBox claimId={claimId} onChanged={onChanged} />
        </div>
        <ClaimPhotosBox claimId={claimId} onChanged={(advanced) => { if (advanced) { setMsg(t('ird.movedReview')); onChanged(); } }} compact />
        <InfoPendingBox claimId={claimId} sourceStage="Waiting Documents" compact />
        {err_}{ok_}
        {updatesCard}
        <ClaimEmailBox claimId={claimId} departmentKey="intake" />
      </div>
    );
  }

  // ---- Waiting Documents ----
  if (status === 'Waiting Documents') {
    return (
      <div style={{ display: 'grid', gap: 8, padding: '4px 0' }}>
        <div style={row}><span className="muted">{t('cci.address')}: </span>{address || '—'}</div>
        {/* Estado REAL del claim mientras espera documentos (no se mueve de su etapa): claim status
            (lifecycle) + payment status. Así se sabe de dónde vino esta copia en Waiting Documents. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ird.claimStatus')}:</span>
          <span className={`badge ${statusColor(summary?.lifecycle_status || null)}`} style={{ fontSize: 'var(--fs-2xs)' }}>{summary?.lifecycle_status || '—'}</span>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>· {t('ird.paymentStatus')}:</span>
          <span className={`badge ${statusColor(summary?.current_status || null)}`} style={{ fontSize: 'var(--fs-2xs)' }}>{summary?.current_status || '—'}</span>
        </div>
        <InfoPendingBox claimId={claimId} sourceStage="Waiting Documents" compact />
        {/* Volver a Claim Review MUEVE el claim → barra de decisión (sólido azul). */}
        <div className="module" style={{ marginTop: 2 }}>
          <DecisionBar label={t('cwa.decision')} hint={t('ird.forceReviewHint')}>
            <button className="btn sm solid-blue" disabled={busy} onClick={forceReview}>→ {t('ird.toClaimReview')}</button>
          </DecisionBar>
        </div>
        {err_}{ok_}
        {updatesCard}
        <ClaimEmailBox claimId={claimId} departmentKey="intake" />
      </div>
    );
  }

  // ---- Claim Review ---- (panel de revisión: fotos, case overview, tormentas, info pending, email + decisiones)
  if (status === 'Claim Review') {
    return (
      <div style={{ display: 'grid', gap: 8, padding: '4px 0' }}>
        {/* El botón "Working on it" vive ahora en la FILA de la cola (visible sin desplegar). */}
        {/* CASE OVERVIEW ARRIBA DE TODO (pedido de los usuarios). Es la conclusión del caso: si
            vive al final, hay que scrollear por diez tarjetas para leer de qué se trata el claim
            antes de decidir — y decidir es justo lo que se hace en esta pantalla. */}
        <CaseOverview claimId={claimId} />
        <div style={row}><span className="muted">{t('cdw.contractor')}: </span>{contractorName || '—'}</div>
        <div style={row}>
          <span className="muted">{t('cci.address')}: </span>{address || '—'}
          {/* Copiar la dirección: en esta pantalla se pega en Canopy, en Hail Trace, en el mapa y
              en el correo al adjuster. Seleccionarla a mano de un renglón es donde se cuelan los
              typos que después mandan el reporte de otra casa. */}
          {address && <CopyBtn value={address} title={t('ird.copyAddress')} />}
        </div>
        {/* ══ DE UNA COLUMNA A DOS ══
            Claim Review era una pila de catorce tarjetas: para decidir había que recorrerla
            entera, y el contrato quedaba a tres pantallas de la póliza que se compara con él.
            Ahora van EN PAREJAS, cada una emparejada con lo que de verdad se lee al lado:
            medidas↔validaciones, info pendiente↔fotos, pérdida↔preguntas del homeowner,
            tormentas↔ATR y —lo que pidieron por nombre— el lector de póliza al lado del
            resumen de póliza. `Par` cae solo a una columna cuando no hay ancho, así que en el
            drawer angosto o en un monitor chico sigue leyéndose igual que antes. */}
        <Par>
          {/* Casa + techo + medidas gratis de Google (como en Supplements). */}
          <PropertyRoofView claimId={claimId} />
          {/* Cuadros de VALIDACIÓN al lado de las medidas: qué falta validar + quién lo hizo. */}
          <ValidationChecklist claimId={claimId} />
        </Par>
        <Par>
          {/* Info Pending recogido por defecto (pedido): se ve el título, se abre si hace falta. */}
          <InfoPendingBox claimId={claimId} sourceStage="Claim Review" compact collapsible />
          <ContractorInvoicesBox claimId={claimId} sourceStage="Claim Review" />
          <ClaimPhotosBox claimId={claimId} compact />
        </Par>
        <Par>
          <ClaimLossInfo claimId={claimId} />
          {/* Homeowner questions: preguntas del intake para irlas llenando en la llamada (editable). */}
          <div id="val-intake"><IntakeSubmissionBox claimId={claimId} /></div>
        </Par>
        {/* Files a lo ancho pero PLEGADO: con 13 archivos se comía la pantalla entera. */}
        <ClaimFiles claimId={claimId} onChanged={onChanged} wide startCollapsed />
        <Par>
          {/* Storm search recogido con preview (patrón del mockup: "NOAA · …"). El div conserva
              el ancla #val-weather que usa el checklist de validaciones para scrollear. */}
          <div id="val-weather">
            <Module id={`storms:${claimId}`} icon={CloudSun} title={t('ird.storms')} preview={t('mod.stormsPreview')} defaultOpen={false}>
              <div style={{ marginTop: 6 }}><ClaimWeather claimId={claimId} /></div>
            </Module>
          </div>
          {/* ATR videos (embebidos + notas) también en Claim Review. */}
          <ClaimAtrBox claimId={claimId} />
        </Par>
        {/* Contrato firmado + validación por AI: a lo ancho porque ADENTRO ya se parte en dos
            (el LOR a la izquierda, los checks a la derecha). */}
        <div id="val-lor"><ContractValidation claimId={claimId} /></div>
        <Par>
          {/* Lector de póliza (Coverage Detector) al lado del resumen que se manda en el card. */}
          <div id="val-policy"><PolicyReader claimId={claimId} /></div>
          <ClaimPolicySummary claimId={claimId} />
        </Par>
        {/* Updates plegado, debajo de Case Overview. */}
        {updatesCard}
        {/* Email recogido por defecto (pedido): se abre solo si lo necesitan. */}
        <Module id={`email:${claimId}`} icon={Mail} title={t('em.communication')} preview={t('mod.emailPreview')} defaultOpen={false}>
          <div style={{ marginTop: 10 }}><ClaimEmailBox claimId={claimId} departmentKey="intake" /></div>
        </Module>
        {/* Barra de decisión (mockup B-working-views): SOLO estos botones llevan color sólido
            fuerte — son los que mueven el claim de etapa. */}
        <div id="val-decision" className="module" style={{ marginTop: 6 }}>
          <DecisionBar label={t('cwa.decision')} hint={t('ird.decideHint')}>
            <button className="btn sm solid-good" disabled={busy} onClick={() => decide('accepted_new')}><Check size={14} strokeWidth={1.75} /> {t('ird.acceptNew')}</button>
            <button className="btn sm solid-blue" disabled={busy} onClick={() => decide('accepted_existing')}><Check size={14} strokeWidth={1.75} /> {t('ird.acceptExisting')}</button>
            <button className="btn sm solid-bad" disabled={busy} onClick={() => setDenying((d) => !d)}><X size={14} strokeWidth={1.75} /> {t('ird.deny')}</button>
            {/* Esperar los documentos de Info Pending: mueve a Waiting Documents y regresa solo a
                Claim Review cuando lleguen todos. Manual, al lado de accepted/denied. */}
            <button className="btn sm solid-warn" disabled={busy} onClick={waitDocs} title={t('ird.waitForDocsHint')}><Hourglass size={14} strokeWidth={1.75} /> {t('ird.waitForDocs')}</button>
          </DecisionBar>
        </div>
        {/* Deny → reason why. Al guardar: sale de Claim Review a "Not Accepted" con el motivo
            (queda en el activity/updates) y dispara la notificación configurada (email/SMS). */}
        {denying && (
          <div className="card" style={{ display: 'grid', gap: 6, borderColor: 'var(--red)' }}>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ird.denyReasonLabel')}</span>
            <textarea className="input" rows={3} value={denyReason} onChange={(e) => setDenyReason(e.target.value)} placeholder={t('ird.denyReasonPh')} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn sm solid-bad" disabled={busy || !denyReason.trim()} onClick={() => decide('denied', denyReason)}><X size={14} strokeWidth={1.75} /> {t('ird.denySave')}</button>
              <button className="btn ghost sm" disabled={busy} onClick={() => { setDenying(false); setDenyReason(''); }}>{t('common.cancel')}</button>
              <ImproveTextButton value={denyReason} onChange={setDenyReason} />
            </div>
          </div>
        )}
        {err_}{ok_}
      </div>
    );
  }

  // ---- Claim Not Accepted (reversible): permite reabrir a Claim Review ----
  if (status === 'Claim Not Accepted') {
    return (
      <div style={{ display: 'grid', gap: 8, padding: '4px 0' }}>
        <div style={row}><span className="muted">{t('cci.address')}: </span>{address || '—'}</div>
        <button className="btn sm" disabled={busy} style={{ justifySelf: 'start' }} onClick={() => forceReview()}>↩ {t('ird.reopenReview')}</button>
        {err_}{ok_}
      </div>
    );
  }

  // Otras etapas de intake: detalle genérico.
  return (
    <div style={{ display: 'grid', gap: 6, padding: '4px 0' }}>
      <div style={row}><span className="muted">{t('cdw.contractor')}: </span>{contractorName || '—'}</div>
      <div style={row}><span className="muted">{t('cci.address')}: </span>{address || '—'}</div>
    </div>
  );
}
